import { useEffect, useState } from 'react';
import { supabase } from '../supabase';
import Layout from '../components/layout';
import Title from '../components/title';
import './categories.css';
import searchIcon from '../assets/search.svg';
import editIcon from '../assets/edit.svg';
import deleteIcon from '../assets/delete.svg';

const CATEGORIES_PER_PAGE = 3;

const Categories = () => {
  const [categories, setCategories] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [isAddCategoryOpen, setIsAddCategoryOpen] = useState(false);
  const [id, setId] = useState('');
  const [editName, setEditName] = useState('');
  const [editNameAr, setEditNameAr] = useState('');
  const [editIconUrl, setEditIconUrl] = useState('');
  const [isEditCategoryOpen, setIsEditCategoryOpen] = useState(false);

  const [name, setName] = useState('');
  const [nameAr, setNameAr] = useState('');
  const [icon, setIcon] = useState('');

  const fetchCategories = async () => {
    const res = await supabase
      .from("Categories")
      .select("*");

    setCategories(res.data);
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  const createCategory = async (e) => {
    e.preventDefault();

    const { data, error } = await supabase.from("Categories").insert([{
      name: name,
      name_ar: nameAr,
      icon: icon,
    }]);

    if (error) {
      alert("Failed to create category: " + error.message);
      return;
    }

    await fetchCategories();

    setName('');
    setNameAr('');
    setIcon('');

    setIsAddCategoryOpen(false);
  };

  const editCategory = (pId, pName, pNameAr, pIcon) => {
    setId(pId);
    setEditName(pName);
    setEditNameAr(pNameAr);
    setEditIconUrl(pIcon);
    setIsEditCategoryOpen(true);
  };

  const updateCategory = async (e) => {
    e.preventDefault();

    await supabase
      .from("Categories")
      .update({
        name: editName,
        name_ar: editNameAr,
        icon: editIconUrl,
      })
      .eq('id', id);

    await fetchCategories();
    setIsEditCategoryOpen(false);
  };

  const deleteCategory = async (id) => {
    await supabase.from("Categories").delete().eq('id', id);
    await fetchCategories();
  };

  const filteredCategories = categories.filter((category) => {
    const value = searchTerm.toLowerCase();

    const matchesSearch = (
      category.id.toString().includes(value) ||
      category.name?.toLowerCase().includes(value) ||
      category.name_ar?.toLowerCase().includes(value)
    );

    return matchesSearch;
  });

  const totalPages = Math.ceil(filteredCategories.length / CATEGORIES_PER_PAGE);
  const startIndex = (currentPage - 1) * CATEGORIES_PER_PAGE;
  const paginatedCategories = filteredCategories.slice(startIndex, startIndex + CATEGORIES_PER_PAGE);

  return (
    <Layout>
      <div className="categories_page">
        <Title
          subtitle="Manage all categories on the Eventra platform"
          title="CATEGORIES MANAGEMENT"
        />

        <div className="categories_controls_card">
          <div className="categories_controls_left">
            <label className="categories_search" htmlFor="categories-search">
              <img alt="" src={searchIcon} />
              <input
                id="categories-search"
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
                placeholder="Search categories..."
                type="text"
                value={searchTerm}
              />
            </label>
          </div>

          <button
            className="categories_add_button"
            onClick={() => setIsAddCategoryOpen(true)}
            type="button"
          >
            <div>+</div>
            <div>Add Category</div>
          </button>
        </div>

        <div className="categories_table_card">
          <div className="categories_table_top">
            <div className="categories_table_title">All Categories</div>
            <div className="categories_table_count">{filteredCategories.length} total categories</div>
          </div>

          <div className="categories_table_scroll">
            <div className="categories_row categories_table_head">
              <div className="categories_checkbox">
                <input type="checkbox" />
              </div>
              <div>ID</div>
              <div>Created At</div>
              <div>Name (EN)</div>
              <div>Name (AR)</div>
              <div>Icon</div>
              <div>Actions</div>
            </div>

            <div className="categories_table_body">
              {paginatedCategories.map((category) => (
                <div className="categories_row" key={category.id}>
                  <div className="categories_checkbox">
                    <input type="checkbox" />
                  </div>

                  <div className="categories_cell_text">{category.id}</div>
                  <div className="categories_cell_text">{category.created_at}</div>

                  <div className="categories_cell_text">{category.name}</div>
                  <div className="categories_cell_text">{category.name_ar}</div>

                  <div className="categories_icon_thumb">
                    <img alt="" src={category.icon} />
                  </div>

                  <div className="categories_actions">
                    <button
                      className="categories_action_button"
                      onClick={() =>
                        editCategory(
                          category.id,
                          category.name,
                          category.name_ar,
                          category.icon
                        )
                      }
                      type="button"
                    >
                      <img alt="" src={editIcon} />
                    </button>
                    <button className="categories_action_button" onClick={() => deleteCategory(category.id)}>
                      <img alt="" src={deleteIcon} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {totalPages > 1 && (
            <div className="categories_pagination">
              <button
                className="categories_pagination_button"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(currentPage - 1)}
              >
                Previous
              </button>
              <div className="categories_pagination_info">
                Page {currentPage} of {totalPages}
              </div>
              <button
                className="categories_pagination_button"
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage(currentPage + 1)}
              >
                Next
              </button>
            </div>
          )}
        </div>

        {isAddCategoryOpen && (
          <div
            className="categories_modal_overlay"
            onClick={() => setIsAddCategoryOpen(false)}
            role="presentation"
          >
            <div
              aria-modal="true"
              className="categories_modal"
              onClick={(event) => event.stopPropagation()}
              role="dialog"
            >
              <div className="categories_modal_header">
                <div className="categories_modal_heading">
                  <h2>ADD NEW CATEGORY</h2>
                  <p>
                    Fill in the details below to create a new category. All fields support
                    bilingual content.
                  </p>
                </div>

                <button
                  aria-label="Close add category overlay"
                  className="categories_modal_close"
                  onClick={() => setIsAddCategoryOpen(false)}
                  type="button"
                >
                  x
                </button>
              </div>

              <form
                className="categories_modal_form"
                onSubmit={createCategory}
              >
                <div className="categories_modal_grid">
                  <label className="categories_modal_field">
                    <span>Name (English)</span>
                    <input
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Category name in English"
                      type="text"
                      value={name}
                    />
                  </label>

                  <label className="categories_modal_field">
                    <span>Name (Arabic)</span>
                    <input
                      dir="rtl"
                      onChange={(e) => setNameAr(e.target.value)}
                      placeholder="اسم الفئة باللغة العربية"
                      type="text"
                      value={nameAr}
                    />
                  </label>

                  <label className="categories_modal_field categories_modal_field_full">
                    <span>Icon URL</span>
                    <input
                      onChange={(e) => setIcon(e.target.value)}
                      placeholder="https://example.com/icon.png"
                      type="text"
                      value={icon}
                    />
                  </label>
                </div>

                <div className="categories_modal_actions">
                  <button
                    className="categories_modal_secondary_button"
                    onClick={() => setIsAddCategoryOpen(false)}
                    type="button"
                  >
                    Cancel
                  </button>
                  <button className="categories_modal_primary_button" type="submit">
                    Create Category
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {isEditCategoryOpen && (
          <div
            className="categories_modal_overlay"
            onClick={() => setIsEditCategoryOpen(false)}
            role="presentation"
          >
            <div
              aria-modal="true"
              className="categories_modal"
              onClick={(event) => event.stopPropagation()}
              role="dialog"
            >
              <div className="categories_modal_header">
                <div className="categories_modal_heading">
                  <h2>EDIT CATEGORY</h2>
                  <p>Update the category details below.</p>
                </div>

                <button
                  aria-label="Close edit category overlay"
                  className="categories_modal_close"
                  onClick={() => setIsEditCategoryOpen(false)}
                  type="button"
                >
                  x
                </button>
              </div>

              <form
                className="categories_modal_form"
                onSubmit={updateCategory}
              >
                <div className="categories_modal_grid">
                  <label className="categories_modal_field">
                    <span>Name (English)</span>
                    <input
                      onChange={(e) => setEditName(e.target.value)}
                      type="text"
                      value={editName}
                    />
                  </label>

                  <label className="categories_modal_field">
                    <span>Name (Arabic)</span>
                    <input
                      dir="rtl"
                      onChange={(e) => setEditNameAr(e.target.value)}
                      type="text"
                      value={editNameAr}
                    />
                  </label>

                  <label className="categories_modal_field categories_modal_field_full">
                    <span>Icon URL</span>
                    <input
                      onChange={(e) => setEditIconUrl(e.target.value)}
                      type="text"
                      value={editIconUrl}
                    />
                  </label>
                </div>

                <div className="categories_modal_actions">
                  <button
                    className="categories_modal_secondary_button"
                    onClick={() => setIsEditCategoryOpen(false)}
                    type="button"
                  >
                    Cancel
                  </button>
                  <button className="categories_modal_primary_button" type="submit">
                    Update Category
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Categories;
